import { Box, Card, TextField, Typography } from "@mui/material"
import { MousePointerClick } from "lucide-react"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { BROCHURE_COLORS, type BrochureSection } from "./stock-images"
import { BROCHURE_FONT, ctaButton, escapeHtml } from "./html-helpers"

// Editor UI for the closing call-to-action band: a bold navy strip with a
// headline, an optional line of supporting text, and the primary button. The
// rendered email HTML is produced by `ctaToHtml`.
interface CtaSectionProps {
  section: BrochureSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function CtaSection({ section, onUpdate }: CtaSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <MousePointerClick size={20} />
          Call to action
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`cta-heading-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Headline</Typography>
              <AiTextImprover fieldType="short" context="brochure closing call-to-action headline" currentText={c.heading} onImprove={(v) => onUpdate(section.id, { heading: v })} />
            </Box>
            <TextField
              fullWidth size="small" id={`cta-heading-${section.id}`}
              value={c.heading || ""}
              onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="e.g., Ready to get started?"
            />
          </Box>

          <Box>
            <Typography component="label" htmlFor={`cta-sub-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Supporting text</Typography>
            <TextField
              fullWidth size="small" id={`cta-sub-${section.id}`}
              value={c.subtext || ""}
              onChange={(e) => onUpdate(section.id, { subtext: e.target.value })}
              placeholder="e.g., Talk to our team today."
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField
              label="Button label" size="small" InputLabelProps={{ shrink: true }}
              value={c.ctaLabel || ""}
              onChange={(e) => onUpdate(section.id, { ctaLabel: e.target.value })}
              placeholder="e.g., Get in touch"
            />
            <TextField
              label="Button link (URL)" size="small" InputLabelProps={{ shrink: true }}
              value={c.ctaUrl || ""}
              onChange={(e) => onUpdate(section.id, { ctaUrl: e.target.value })}
              placeholder="https://xopnetworks.com/contact"
              helperText="Leave blank to hide the button"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Serialize the CTA band to email HTML: a centered navy strip with the headline,
// optional subtext, and the lime pill button. Returns '' if there's nothing to
// show.
export function ctaToHtml(section: BrochureSection): string {
  const c = section.content || {}
  const heading = c.heading ? escapeHtml(c.heading) : ""
  const subtext = c.subtext ? escapeHtml(c.subtext) : ""
  const button = ctaButton(c.ctaUrl || "", c.ctaLabel || "")

  if (!heading && !subtext && !button) return ""

  return `
          <!-- Call to action -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.navy}" align="center" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.navy}; padding: 44px 40px; text-align: center;">
              ${heading ? `<h2 style="margin: 0 0 ${subtext || button ? '12px' : '0'} 0; color: ${BROCHURE_COLORS.white}; font-family: ${BROCHURE_FONT}; font-size: 26px; line-height: 32px; font-weight: bold;">${heading}</h2>` : ''}
              ${subtext ? `<p style="margin: 0 0 ${button ? '26px' : '0'} 0; color: #C7D0DE; font-family: ${BROCHURE_FONT}; font-size: 16px; line-height: 24px;">${subtext}</p>` : ''}
              ${button ? `<table cellpadding="0" cellspacing="0" border="0" role="presentation" align="center"><tr><td>${button}</td></tr></table>` : ''}
            </td>
          </tr>
          `
}
